import { useEffect, useRef, useState } from "react";
import type { SailPlotExtensionContext } from "@heiselanalytics/sailplot";
import { ChangelogContent } from "../components/ChangelogContent";
import { platformVersion } from "../config/version";
import {
  getImprovementStatus,
  submitImprovement,
  type ImprovementStatus,
} from "../integrations/adminApi";
import { PageActions } from "./PageActions";

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() || null;
const newGitHubIssueUrl =
  "https://github.com/HeiselAnalytics/sailplot/issues/new";

const copy = {
  en: {
    eyebrow: "Shape SailPlot",
    title: "Suggest an improvement.",
    intro:
      "Found a clearer way to support sailors? Send us your suggestion. We review it and publish accepted ideas as trackable GitHub issues.",
    nameLabel: "Name",
    namePlaceholder: "Your name",
    emailLabel: "Email",
    emailPlaceholder: "name@example.com",
    titleLabel: "Title",
    titlePlaceholder: "A short summary of the improvement",
    descriptionLabel: "Description",
    descriptionPlaceholder:
      "What should change, when would you use it, and what would make it helpful?",
    plotTitle: "Troubleshooting context",
    includeCurrentPlotLabel: "Include my current plot for troubleshooting",
    includeCurrentPlotHelp:
      "The plot is stored privately for review and is never added to the public GitHub issue.",
    noCurrentPlot:
      "No current plot is available. Open this page from an editor containing a plot to include it.",
    publicationTitle: "Publication in the GitHub issue",
    publishNameLabel: "Mention my name in the public GitHub issue",
    publishNameHelp:
      "Leave this unchecked to publish anonymously. Your email address is never published.",
    publicNameLabel: "Name shown publicly",
    publicNamePlaceholder: "Name to display publicly",
    privacy:
      "Your name and email are stored so we can review the suggestion and contact you if needed. Do not include confidential or sensitive information in the description.",
    submit: "Send improvement",
    sending: "Sending...",
    successPageTitle: "Improvement sent",
    successIntro: "Your suggestion has reached the SailPlot team.",
    successEyebrow: "Thank you",
    successTitle: "Thank you. Your idea is on deck.",
    successText: "We appreciate you taking the time to help improve SailPlot.",
    nextEyebrow: "What happens next",
    nextTitle: "From suggestion to improvement",
    nextSteps: [
      "We review your suggestion carefully.",
      "If it fits SailPlot's concept, we publish it as a public GitHub issue.",
      "We prioritize accepted ideas and implement them as quickly as possible.",
    ],
    trackingTitle: "Track your suggestion",
    trackingPending:
      "Review and GitHub publication are still in progress. This page updates automatically.",
    trackingRejected:
      "We reviewed the suggestion and will not publish it as a GitHub issue in its current form.",
    trackingAction: "Open the GitHub issue",
    reference: "Reference",
    again: "Send another idea",
    error: "The request could not be sent. Please try again.",
    captchaMissing:
      "The abuse check is not configured. Please contact the site operator.",
    githubEyebrow: "Direct on GitHub",
    githubTitle: "Publish it yourself",
    githubText:
      "Already use GitHub? Create your suggestion directly as a public issue. You can join the discussion and track its progress there from the start.",
    githubPrivacy:
      "Everything you enter on GitHub is public. Do not include email addresses, personal data, or confidential information.",
    githubAction: "Create GitHub issue",
    feedbackFridayEyebrow: "Feedback Friday",
    feedbackFridayTitle: "Built with sailors, every Friday.",
    feedbackFridayText:
      "Feedback Friday is SailPlot's recurring community feedback loop. We review suggestions, turn selected ideas into improvements, and identify these releases in the changelog together with the users who contributed them.",
  },
  de: {
    eyebrow: "SailPlot mitgestalten",
    title: "Verbesserungsvorschlag",
    intro:
      "Du hast eine Idee, die Seglern noch besser hilft? Sende uns deinen Vorschlag. Wir prüfen ihn und veröffentlichen angenommene Ideen als nachverfolgbares GitHub Issue.",
    nameLabel: "Name",
    namePlaceholder: "Dein Name",
    emailLabel: "E-Mail-Adresse",
    emailPlaceholder: "name@beispiel.de",
    titleLabel: "Titel",
    titlePlaceholder: "Kurze Zusammenfassung der Verbesserung",
    descriptionLabel: "Beschreibung",
    descriptionPlaceholder:
      "Was soll sich ändern, wann würdest du es nutzen und wodurch wird es hilfreich?",
    plotTitle: "Kontext zur Fehlerprüfung",
    includeCurrentPlotLabel:
      "Meinen aktuellen Plot zur Fehlerprüfung mitsenden",
    includeCurrentPlotHelp:
      "Der Plot wird nur privat zur Prüfung gespeichert und niemals in das öffentliche GitHub Issue übernommen.",
    noCurrentPlot:
      "Es ist kein aktueller Plot verfügbar. Öffne diese Seite aus einem Editor mit Plot, um ihn mitsenden zu können.",
    publicationTitle: "Veröffentlichung im GitHub Issue",
    publishNameLabel: "Meinen Namen im öffentlichen GitHub Issue nennen",
    publishNameHelp:
      "Ohne Haken veröffentlichen wir den Vorschlag anonym. Deine E-Mail-Adresse wird niemals veröffentlicht.",
    publicNameLabel: "Öffentlich sichtbarer Name",
    publicNamePlaceholder: "Name für die öffentliche Anzeige",
    privacy:
      "Name und E-Mail-Adresse speichern wir zur Prüfung und für mögliche Rückfragen. Bitte trage in der Beschreibung keine vertraulichen oder sensiblen Informationen ein.",
    submit: "Verbesserung senden",
    sending: "Wird gesendet...",
    successPageTitle: "Verbesserungsvorschlag gesendet",
    successIntro: "Dein Vorschlag hat das SailPlot-Team erreicht.",
    successEyebrow: "Vielen Dank",
    successTitle: "Danke. Deine Idee ist an Bord.",
    successText:
      "Danke, dass du dir die Zeit nimmst und uns dabei hilfst, SailPlot noch besser zu machen.",
    nextEyebrow: "So geht es weiter",
    nextTitle: "Vom Vorschlag zur Verbesserung",
    nextSteps: [
      "Wir prüfen deinen Vorschlag sorgfältig.",
      "Wenn er zum Konzept von SailPlot passt, veröffentlichen wir ihn als öffentliches GitHub Issue.",
      "Angenommene Ideen priorisieren wir und setzen sie schnellstmöglich um.",
    ],
    trackingTitle: "Vorschlag verfolgen",
    trackingPending:
      "Prüfung und Veröffentlichung auf GitHub laufen noch. Diese Seite aktualisiert sich automatisch.",
    trackingRejected:
      "Wir haben den Vorschlag geprüft und veröffentlichen ihn in dieser Form nicht als GitHub Issue.",
    trackingAction: "GitHub Issue öffnen",
    reference: "Referenz",
    again: "Weitere Idee senden",
    error:
      "Die Anfrage konnte nicht gesendet werden. Bitte versuche es erneut.",
    captchaMissing:
      "Die Missbrauchsprüfung ist nicht konfiguriert. Bitte kontaktiere den Seitenbetreiber.",
    githubEyebrow: "Direkt auf GitHub",
    githubTitle: "Selbst veröffentlichen",
    githubText:
      "Du nutzt bereits GitHub? Dann kannst du deinen Vorschlag direkt als öffentliches Issue erstellen, mitdiskutieren und den Fortschritt von Anfang an verfolgen.",
    githubPrivacy:
      "Alle Angaben auf GitHub sind öffentlich. Trage dort keine E-Mail-Adressen, persönlichen Daten oder vertraulichen Informationen ein.",
    githubAction: "GitHub Issue erstellen",
    feedbackFridayEyebrow: "Feedback Friday",
    feedbackFridayTitle: "Freitags gemeinsam mit Seglern weiterentwickelt.",
    feedbackFridayText:
      "Feedback Friday ist SailPlots regelmässiger Austausch mit der Community. Wir prüfen Vorschläge, entwickeln ausgewählte Ideen weiter und kennzeichnen diese Releases im Changelog gemeinsam mit den Nutzerinnen und Nutzern, von denen sie stammen.",
  },
} as const;

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-sailplot-turnstile="true"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.sailplotTurnstile = "true";
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(), { once: true });
    document.head.append(script);
  });
};

export function ImprovementPage({
  language,
  navigate,
}: SailPlotExtensionContext) {
  const text = copy[language];
  const widget = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string>();
  const [token, setToken] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [publishName, setPublishName] = useState(false);
  const [includeCurrentPlot, setIncludeCurrentPlot] = useState(false);
  const [publicName, setPublicName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState("");
  const [submission, setSubmission] = useState<ImprovementStatus | null>(null);
  const submissionId = submission?.improvementId ?? null;
  const issueUrl = submission?.issueUrl ?? null;
  const currentPlotUrl = window.location.hash
    ? `${window.location.origin}/${window.location.hash}`
    : null;

  useEffect(() => {
    if (!siteKey || !widget.current) return;
    let cancelled = false;
    void loadTurnstile()
      .then(() => {
        if (cancelled || !widget.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(widget.current, {
          sitekey: siteKey,
          action: "improvement",
          theme:
            document.documentElement.dataset.theme === "dark"
              ? "dark"
              : "light",
          callback: (value) => setToken(value),
          "expired-callback": () => setToken(""),
          "error-callback": () => setToken(""),
        });
      })
      .catch(() => setError(text.error));
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
      }
    };
  }, [text.error]);

  useEffect(() => {
    if (!submissionId || issueUrl || submission?.status === "REJECTED") return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const next = await getImprovementStatus(submissionId);
        if (!cancelled) setSubmission(next);
      } catch {
        // The submission remains safely stored even if a status refresh fails.
      }
    };
    const interval = window.setInterval(() => void refresh(), 4_000);
    void refresh();
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [issueUrl, submission?.status, submissionId]);

  useEffect(() => {
    if (state !== "sent") return;
    window.scrollTo({ top: 0, behavior: "auto" });
    document
      .querySelector<HTMLElement>(".extension-page-shell")
      ?.scrollTo({ top: 0, behavior: "auto" });
  }, [state]);

  const reset = () => {
    setName("");
    setEmail("");
    setPublishName(false);
    setIncludeCurrentPlot(false);
    setPublicName("");
    setTitle("");
    setDescription("");
    setState("idle");
    setError("");
    setToken("");
    setSubmission(null);
    if (widgetId.current) window.turnstile?.reset(widgetId.current);
  };

  const send = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (includeCurrentPlot && !currentPlotUrl) {
      setError(text.noCurrentPlot);
      return;
    }
    if (!siteKey) {
      setError(text.captchaMissing);
      return;
    }
    if (!token) {
      setError(text.error);
      return;
    }
    setState("sending");
    setError("");
    try {
      const response = await submitImprovement({
        name,
        email,
        publishName,
        language,
        includeCurrentPlot,
        currentPlotUrl:
          includeCurrentPlot && currentPlotUrl ? currentPlotUrl : undefined,
        publicName: publishName ? publicName : undefined,
        title,
        description,
        turnstileToken: token,
        context: {
          source: window.location.hostname,
          version: platformVersion,
          currentUrl: `${window.location.origin}${window.location.pathname}`,
        },
      });
      setSubmission({
        success: true,
        improvementId: response.improvementId,
        status: response.status,
        issueUrl: null,
      });
      setState("sent");
    } catch (submissionError) {
      setError(
        submissionError instanceof Error ? submissionError.message : text.error,
      );
      setState("idle");
      setToken("");
      if (widgetId.current) window.turnstile?.reset(widgetId.current);
    }
  };

  return (
    <article className="platform-page platform-reference-page improvement-page">
      <header>
        <span className="platform-page-eyebrow">{text.eyebrow}</span>
        <h1>{state === "sent" ? text.successPageTitle : text.title}</h1>
        <p>{state === "sent" ? text.successIntro : text.intro}</p>
      </header>

      <section className="improvement-feedback-friday">
        <div>
          <span>{text.feedbackFridayEyebrow}</span>
          <h2>{text.feedbackFridayTitle}</h2>
        </div>
        <p>{text.feedbackFridayText}</p>
      </section>

      <div className="improvement-content-grid">
        {state === "sent" ? (
          <div className="improvement-success">
            <section className="improvement-thank-you" aria-live="polite">
              <span>{text.successEyebrow}</span>
              <h2>{text.successTitle}</h2>
              <p>{text.successText}</p>
            </section>
            {submission && (
              <section className="improvement-tracking" aria-live="polite">
                <h2>{text.trackingTitle}</h2>
                <p>
                  {text.reference}: <code>{submission.improvementId}</code>
                </p>
                {submission.issueUrl ? (
                  <a
                    className="primary-button"
                    href={submission.issueUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {text.trackingAction} ↗
                  </a>
                ) : submission.status === "REJECTED" ? (
                  <p>{text.trackingRejected}</p>
                ) : (
                  <p>{text.trackingPending}</p>
                )}
              </section>
            )}
            <div className="platform-page-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={reset}
              >
                {text.again}
              </button>
            </div>
          </div>
        ) : (
          <form
            className="improvement-form"
            onSubmit={(event) => void send(event)}
          >
            <div className="improvement-contact-fields">
              <label>
                <span>{text.nameLabel}</span>
                <input
                  required
                  minLength={2}
                  maxLength={120}
                  autoComplete="name"
                  value={name}
                  placeholder={text.namePlaceholder}
                  onChange={(event) => {
                    const nextName = event.target.value;
                    setPublicName((current) =>
                      !current || current === name ? nextName : current,
                    );
                    setName(nextName);
                  }}
                />
              </label>
              <label>
                <span>{text.emailLabel}</span>
                <input
                  required
                  type="email"
                  maxLength={254}
                  autoComplete="email"
                  value={email}
                  placeholder={text.emailPlaceholder}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
            </div>
            <label>
              <span>{text.titleLabel}</span>
              <input
                required
                minLength={4}
                maxLength={120}
                value={title}
                placeholder={text.titlePlaceholder}
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <label>
              <span>{text.descriptionLabel}</span>
              <textarea
                required
                minLength={20}
                maxLength={8000}
                rows={8}
                value={description}
                placeholder={text.descriptionPlaceholder}
                onChange={(event) => setDescription(event.target.value)}
              />
            </label>
            <fieldset
              className={`improvement-publication improvement-plot-context${
                currentPlotUrl ? "" : " is-unavailable"
              }`}
            >
              <legend>{text.plotTitle}</legend>
              <label
                className={`improvement-checkbox${
                  currentPlotUrl ? "" : " is-disabled"
                }`}
              >
                <input
                  type="checkbox"
                  checked={includeCurrentPlot}
                  disabled={!currentPlotUrl}
                  onChange={(event) =>
                    setIncludeCurrentPlot(event.target.checked)
                  }
                />
                <span>{text.includeCurrentPlotLabel}</span>
              </label>
              <small>
                {currentPlotUrl
                  ? text.includeCurrentPlotHelp
                  : text.noCurrentPlot}
              </small>
            </fieldset>
            <fieldset className="improvement-publication">
              <legend>{text.publicationTitle}</legend>
              <label className="improvement-checkbox">
                <input
                  type="checkbox"
                  checked={publishName}
                  onChange={(event) => setPublishName(event.target.checked)}
                />
                <span>{text.publishNameLabel}</span>
              </label>
              <small>{text.publishNameHelp}</small>
              {publishName && (
                <label>
                  <span>{text.publicNameLabel}</span>
                  <input
                    required
                    minLength={2}
                    maxLength={120}
                    value={publicName}
                    placeholder={text.publicNamePlaceholder}
                    onChange={(event) => setPublicName(event.target.value)}
                  />
                </label>
              )}
            </fieldset>
            <small>{text.privacy}</small>
            {siteKey ? (
              <div ref={widget} className="improvement-turnstile" />
            ) : null}
            {error ? (
              <p className="improvement-error" role="alert">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              className="primary-button"
              disabled={state === "sending" || !token}
            >
              {state === "sending" ? text.sending : text.submit}
            </button>
          </form>
        )}

        {state === "sent" ? (
          <aside className="improvement-next-steps">
            <span>{text.nextEyebrow}</span>
            <h2>{text.nextTitle}</h2>
            <ol>
              {text.nextSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </aside>
        ) : (
          <aside className="improvement-github-box">
            <svg
              className="improvement-github-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M12 1C5.923 1 1 5.923 1 12c0 4.867 3.149 8.979 7.521 10.436.55.096.756-.233.756-.522 0-.262-.014-1.128-.014-2.049-2.763.509-3.479-.674-3.699-1.293-.124-.317-.66-1.293-1.128-1.554-.385-.207-.935-.715-.014-.729.866-.014 1.485.797 1.691 1.128.99 1.663 2.571 1.196 3.204.907.096-.715.385-1.196.701-1.471-2.447-.275-5.005-1.224-5.005-5.432 0-1.196.426-2.186 1.128-2.956-.11-.275-.495-1.402.11-2.915 0 0 .921-.289 3.025 1.128A10.2 10.2 0 0 1 12 6.306a10.2 10.2 0 0 1 2.75.371c2.104-1.43 3.025-1.128 3.025-1.128.605 1.513.22 2.64.11 2.915.701.77 1.128 1.746 1.128 2.956 0 4.222-2.571 5.157-5.019 5.432.399.344.743 1.004.743 2.035 0 1.471-.014 2.654-.014 3.025 0 .289.206.632.756.522A11.02 11.02 0 0 0 23 12c0-6.077-4.923-11-11-11Z" />
            </svg>
            <span>{text.githubEyebrow}</span>
            <h2>{text.githubTitle}</h2>
            <p>{text.githubText}</p>
            <a
              className="primary-button improvement-github-button"
              href={newGitHubIssueUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 1C5.923 1 1 5.923 1 12c0 4.867 3.149 8.979 7.521 10.436.55.096.756-.233.756-.522 0-.262-.014-1.128-.014-2.049-2.763.509-3.479-.674-3.699-1.293-.124-.317-.66-1.293-1.128-1.554-.385-.207-.935-.715-.014-.729.866-.014 1.485.797 1.691 1.128.99 1.663 2.571 1.196 3.204.907.096-.715.385-1.196.701-1.471-2.447-.275-5.005-1.224-5.005-5.432 0-1.196.426-2.186 1.128-2.956-.11-.275-.495-1.402.11-2.915 0 0 .921-.289 3.025 1.128A10.2 10.2 0 0 1 12 6.306a10.2 10.2 0 0 1 2.75.371c2.104-1.43 3.025-1.128 3.025-1.128.605 1.513.22 2.64.11 2.915.701.77 1.128 1.746 1.128 2.956 0 4.222-2.571 5.157-5.019 5.432.399.344.743 1.004.743 2.035 0 1.471-.014 2.654-.014 3.025 0 .289.206.632.756.522A11.02 11.02 0 0 0 23 12c0-6.077-4.923-11-11-11Z" />
              </svg>
              {text.githubAction}
            </a>
            <small>{text.githubPrivacy}</small>
          </aside>
        )}
      </div>

      <section className="improvement-changelog">
        <ChangelogContent language={language} embedded />
      </section>

      <PageActions language={language} navigate={navigate} />
    </article>
  );
}
